// Tenant + origin validation for the B2B portal. Single source of truth shared
// by both build targets. Mirrors the server-side contracts (api/, committed on
// develop):
//   - tenant slug charset:  ^[a-z0-9-]+$  length 1–63
//   - allowed portal origin: ^https://[a-z0-9-]+\.coffeechats\.me$ (single
//     label, https)
//   - RESERVED_SLUGS: first-label subdomains that are NOT tenants (so on
//     admin.coffeechats.me the first label 'admin' resolves to NO tenant and
//     the console must not send X-Institution-Slug).
//
// Pure functions, no imports — safe to import from either module graph.

const SLUG_RE = /^[a-z0-9-]{1,63}$/;
const PORTAL_ORIGIN_RE = /^https:\/\/[a-z0-9-]+\.coffeechats\.me$/;

/** Reserved first-label subdomains that never map to a tenant. */
export const RESERVED_SLUGS = new Set([
  'admin', 'api', 'www', 'app', 'portal', 'meet', 'mail', 'status', 'help',
  'support', 'blog', 'docs', 'assets', 'cdn', 'static', 'auth', 'login',
  'dashboard', 'book', 'booking', 'demo', 'staging', 'test', 'dev', 'internal',
  'mcp', 'go', 'send',
]);

/**
 * True iff `slug` matches the tenant charset (^[a-z0-9-]$, length 1–63).
 * Does NOT check reservation — compose with RESERVED_SLUGS at call sites that
 * need "usable tenant".
 * @param {string} slug
 * @returns {boolean}
 */
export function isValidTenantSlug(slug) {
  return typeof slug === 'string' && SLUG_RE.test(slug);
}

/**
 * True iff `origin` is an allowed single-label https portal origin, e.g.
 * `https://dartmouth.coffeechats.me`. Rejects the apex, nested labels, and http.
 * @param {string} origin
 * @returns {boolean}
 */
export function isAllowedPortalOrigin(origin) {
  return typeof origin === 'string' && PORTAL_ORIGIN_RE.test(origin);
}

/**
 * Resolve the active tenant slug from a hostname's first label.
 *
 * Rules:
 *   - In dev, VITE_TENANT_OVERRIDE (when set + valid + not reserved) wins so a
 *     `localhost` session can impersonate a tenant.
 *   - A bare host with no dot (e.g. `localhost`) has no tenant → null.
 *   - The first label must be a valid slug AND not reserved (so
 *     `admin.coffeechats.me` → null).
 *
 * @param {string} [hostname=window.location.hostname]
 * @returns {string|null}
 */
export function tenantSlugFromHostname(hostname = (typeof window !== 'undefined' ? window.location.hostname : '')) {
  const override = typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env.VITE_TENANT_OVERRIDE
    : undefined;
  if (override) {
    const o = String(override).toLowerCase();
    return isValidTenantSlug(o) && !RESERVED_SLUGS.has(o) ? o : null;
  }

  if (typeof hostname !== 'string' || !hostname) {
    return null;
  }
  const host = hostname.toLowerCase().split(':')[0];
  const labels = host.split('.');
  if (labels.length < 2) {
    // Bare host (localhost, an IP without a domain, …) — no tenant.
    return null;
  }
  const first = labels[0];
  if (!isValidTenantSlug(first) || RESERVED_SLUGS.has(first)) {
    return null;
  }
  return first;
}

/**
 * Open-redirect guard for the /auth hop: sanitize a caller-supplied return
 * slug. Returns the slug only when it is a valid, non-reserved tenant slug;
 * otherwise null (never a URL, never a path).
 * @param {string} raw
 * @returns {string|null}
 */
export function sanitizeReturnSlug(raw) {
  if (!isValidTenantSlug(raw)) {
    return null;
  }
  return RESERVED_SLUGS.has(raw) ? null : raw;
}
